/* eslint react-hooks/exhaustive-deps: "warn" */

import React from 'react';

import cn from 'classnames';

import { number } from 'libs/number';

import styles from './AnimatedNumber.module.scss';

const baseClass = 'gdb-animated-number';

type Props = {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
};

export const AnimatedNumber = ({ value, duration = 2000, prefix = '', suffix = '', className }: Props) => {
  const ref = React.useRef<HTMLParagraphElement>(null);
  const valueRef = React.useRef<HTMLSpanElement>(null);

  const [hasIntersected, setHasIntersected] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [hasIntersected]);

  React.useEffect(() => {
    const valueRefCurrent = valueRef.current;

    if (!valueRefCurrent || !hasIntersected) {
      return;
    }

    let rafId = 0;
    let startTimestamp: number | null = null;

    valueRefCurrent.innerText = '0';

    const step = (timestamp: number) => {
      if (startTimestamp === null) {
        startTimestamp = timestamp;
      }

      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      valueRefCurrent.innerText = String(number(Math.floor(progress * value)));

      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      }
    };

    rafId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [value, duration, hasIntersected]);

  return (
    <p ref={ref} className={cn(styles[`${baseClass}-wrapper`], className)}>
      {prefix}
      <span ref={valueRef}>0</span>
      {suffix}
    </p>
  );
};
